import {useEffect, useState} from 'react';
import {Linking} from 'react-native';
import {getShareInfo, updateInviteCode} from './share.service';
import globalStore from '@/services/global.state';
import {errorLog, goTo, inApp, openShare} from '@/utils';
import {copyToClipboard} from '@/utils/clipboard';
import i18n from '@/i18n';
import {ShareInfo} from '@/components/business/share-panel/share-panel-provider';
import {buildInviteShareUrl, webConfig} from '@/services/web-config.state';
import {getUrl as getScratchUrl} from '@/pages/new-scratch/const';
import {useClipboard} from './use-clipboard';
import {InviteSource} from '@/utils/invite-attribution';

const makeShareMessage = (
  msg: string,
  inviteCode: string,
  inviteSource?: InviteSource,
) => {
  const shareUrl = buildInviteShareUrl(
    webConfig.shareUrl,
    inviteCode,
    undefined,
    inviteSource,
  );
  return shareUrl ? msg.replace(/http(.*)$/g, '').concat(shareUrl) : '';
};

function shareMessage(shareText: string, info: ShareInfo) {
  if (!globalStore.token) {
    goTo('Login');
    return;
  }
  if (!shareText) {
    return;
  }
  if (inApp && openShare(shareText)) {
    return;
  }
  globalStore.shareProviderSubject.next({...info, info: shareText});
}

export function shareInvitation(
  inviteText: string,
  code: string,
  info: ShareInfo = {},
  inviteSource?: InviteSource,
) {
  shareMessage(makeShareMessage(inviteText, code, inviteSource), {
    ...info,
    inviteCode: code,
  });
}

function copyWithFeedback(
  content: string,
  tip = '',
  showSuccess = true,
): Promise<boolean> {
  if (!content) {
    globalStore.globalWaringTotal(i18n.t('share.copyFailed'));
    return Promise.resolve(false);
  }
  // Start copying synchronously inside the click handler to preserve browser user activation.
  return copyToClipboard(content, inApp).then(copied => {
    if (!copied) {
      globalStore.globalWaringTotal(i18n.t('share.copyFailed'));
      return false;
    }
    if (showSuccess) {
      globalStore.globalSucessTotal(i18n.t('share.copy-success'), tip);
    }
    return true;
  });
}

export function shareToPlatform(
  platform: string,
  link: string,
  tip: string = '',
) {
  if (!globalStore.token) {
    goTo('Login');
    return;
  }
  if (!link) {
    globalStore.globalWaringTotal(i18n.t('share.copyFailed'));
    return false;
  }
  // Keep opening the destination within the click gesture, before awaiting copy.
  const copyResult = copyWithFeedback(
    link,
    tip,
    platform === 'link' || platform === 'CopyLink',
  );
  switch (platform) {
    case 'link':
    case 'CopyLink':
      return copyResult;
    case 'Facebook':
      Linking.openURL(
        inApp
          ? 'fb://messaging'
          : `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
              link.match(/https?:\/\/\S+/)?.[0] || link,
            )}`,
      ).catch(e => errorLog(e));
      break;
    case 'Telegram': {
      const url = link.match(/https?:\/\/\S+/)?.[0] || link;
      const text = link.replace(url, '').trim();
      Linking.openURL(
        inApp
          ? `tg://msg_url?url=${encodeURIComponent(link)}`
          : `https://t.me/share/url?url=${encodeURIComponent(
              url,
            )}&text=${encodeURIComponent(text)}`,
      ).catch(e => errorLog(e));
      break;
    }
    case 'Whatsapp':
      Linking.openURL('https://wa.me?text=' + encodeURIComponent(link)).catch(
        e => errorLog(e),
      );
      break;
    case 'Instagram':
      Linking.openURL(
        inApp ? 'instagram://media' : 'https://www.instagram.com/',
      ).catch(e => errorLog(e));
      break;
  }
}

export function useShare(autoInit = false) {
  const [code, setCode] = useState<string>('');
  const [inviteText, setInviteText] = useState('');
  const {copy} = useClipboard();

  const copyCode = () => {
    if (!globalStore.token) {
      goTo('Login');
      return;
    }
    if (!code) {
      globalStore.globalWaringTotal(i18n.t('share.copyFailed'));
      return;
    }
    copy(code).then(copied => {
      if (copied) {
        globalStore.globalSucessTotal(i18n.t('share.copy-success'));
      } else {
        globalStore.globalWaringTotal(i18n.t('share.copyFailed'));
      }
    });
  };

  /**
   * 进行分享
   * @param info
   *
   */
  const doShare = (info: ShareInfo = {}, inviteSource?: InviteSource) => {
    shareInvitation(inviteText, code, info, inviteSource);
  };

  const initShare = () => {
    return getShareInfo().then(data => {
      setCode(data.inviteCode);
      setInviteText(data.inviteRegisterLinkUrl);
    });
  };

  const refreshCode = async () => {
    await updateInviteCode();
    await initShare();
  };
  const shareGame = (info: ShareInfo = {}) => {
    shareMessage(getScratchUrl('/scratch-share', {code}), {
      ...info,
      inviteCode: code,
    });
  };

  useEffect(() => {
    if (autoInit) {
      initShare();
    }
  }, [autoInit]);

  return {
    initShare,
    doShare,
    code,
    refreshCode,
    copyCode,
    shareGame,
  };
}
